import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  CircularProgress,
  Box,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  TableHead,
  TableRow,
  Button,
  Paper,
  Chip
} from '@mui/material';
import {
  getPatientHistory,
  getPatientById
} from '../api/patientApi';
import { getAllPatientReports } from '../api/answersApi';
import { getActiveAssessment } from '../api/saasApi';
import { useMe } from '../context/MeContext';
import BarChartLevel from '../components/BarChartLevel';
import { getCategory } from '../api/categoryApi';
import { PageHeader } from '../components/ui';

const PatientHistory = () => {
  const { patientId } = useParams();
  const [history, setHistory] = useState([]);
  // All domains, so the chart shows every domain (bars only where assessed).
  const [domainNames, setDomainNames] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCategory()
      .then((res) => { if (!cancelled) setDomainNames((res.data?.data || []).map((c) => c.name).filter(Boolean)); })
      .catch(() => { /* fall back to assessed domains only */ });
    return () => { cancelled = true; };
  }, []);
  const [loading, setLoading] = useState(true);
  const [patientLoading, setPatientLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);
  const [patientDetails, setPatientDetails] = useState(null);
  const [reports, setReports] = useState([]);
  const navigate = useNavigate();

  // While a paid assessment is in progress, reports dated on/after its payment
  // hold in-progress data: they are only viewable via "Finish & view report".
  const { billing, isExempt, isPlatformAdmin } = useMe();
  const gated = !!billing && !isExempt && !isPlatformAdmin;
  const [openSince, setOpenSince] = useState(null); // 'YYYY-MM-DD' or null

  useEffect(() => {
    if (!patientId || !gated) { setOpenSince(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getActiveAssessment(patientId);
        const a = res.data?.data;
        if (!cancelled) {
          setOpenSince(a?.status === 'Paid' ? String(a.paidAt || a.createdAt || '0000-00-00').slice(0, 10) : null);
        }
      } catch (e) {
        // Fail closed: hide every report link if the status is unknown.
        if (!cancelled) setOpenSince('0000-00-00');
      }
    })();
    return () => { cancelled = true; };
  }, [patientId, gated]);

  const isInProgress = (report) =>
    !!openSince && String(report.createdAt || '').slice(0, 10) >= openSince;

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        let patientData = null;

        if (!patientData) {
          try {
            const res = await getPatientById(patientId);
            if (res.data?.succeeded && res.data.data) {
              const p = res.data.data;
              patientData = {
                id: p.id,
                name: p.name,
                mobileNo: p.mobileNo,
                dateOfBirth: p.dateOfBirth,
                gender: p.gender || '-',
                city: p.city || '-',
                state: p.state || '-',
                source: 'local',
              };
            }
          } catch (err) {
            console.warn('Local fetch failed:', err);
          }
        }

        setPatientDetails(patientData);
      } catch (err) {
        console.error('Error fetching Patient:', err);
      } finally {
        setPatientLoading(false);
      }
    };

    fetchPatient();
  }, [patientId]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await getPatientHistory(patientId);
        if (res.data.succeeded) {
          setHistory(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [patientId]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await getAllPatientReports(patientId);
        if (res.data.succeeded) {
          setReports(res.data.data);
        }
      } catch (error) {
        console.error('Failed to load reports:', error);
      } finally {
        setReportLoading(false);
      }
    };

    if (patientId) fetchReports();
  }, [patientId]);

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleViewReport = (clickedReport) => {
    const sortedReports = [...reports].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    const currentIndex = sortedReports.findIndex(
      (r) => r.createdAt === clickedReport.createdAt
    );

    const reportDataArray = sortedReports.slice(currentIndex);

    navigate(`/patient/${patientId}/report`, {
      state: {
        patientId,
        patientName: patientDetails ? patientDetails.name : '-',
        staffName: clickedReport.staffName,
        reportDataArray,
        // Only the newest report may offer "Finish assessment".
        isLatest: currentIndex === 0,
      },
    });
  };

  return (
    <Box>
      <PageHeader
        title="Assessment history"
        subtitle={patientDetails?.name}
        actions={
          <Button variant="outlined" onClick={() => navigate(`/patient/${patientId}`)}>
            Patient record
          </Button>
        }
      />

      {patientLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : !patientDetails ? (
        <Typography>Invalid Patient</Typography>
      ) : (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography><strong>Name:</strong> {patientDetails.name}</Typography>
            <Typography><strong>Mobile:</strong> {patientDetails.mobileNo}</Typography>
            {patientDetails.dateOfBirth && (
              <>
                <Typography>
                  <strong>DOB:</strong> {new Date(patientDetails.dateOfBirth).toLocaleDateString()}
                </Typography>
                <Typography>
                  <strong>Age:</strong> {calculateAge(patientDetails.dateOfBirth)} years
                </Typography>
              </>
            )}
            <Typography><strong>City:</strong> {patientDetails.city}</Typography>
            <Typography><strong>State:</strong> {patientDetails.state}</Typography>
            <Typography color="text.secondary">
              <em>Source: {patientDetails.source}</em>
            </Typography>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : history.length === 0 ? (
        <Typography>No assessments found on selected date.</Typography>
      ) : (
        <BarChartLevel data={history} domains={domainNames || undefined} />
      )}

      {reportLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Typography>No assessments found on selected date.</Typography>
      ) : (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 2 }}>
            All Reports
          </Typography>

          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(report.createdAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>
                      {isInProgress(report) ? (
                        <Chip size="small" color="info" label="Assessment in progress" />
                      ) : (
                        <Button
                          variant="outlined"
                          onClick={() => handleViewReport(report)}
                        >
                          View Report
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default PatientHistory;
